/** @jsxImportSource @emotion/react */
// React/core
import React, { useState } from 'react'
// Third-party
import { css } from '@emotion/react'
import { DatePicker } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Icon from 'src/components/Icon'
import { FilterDropdown } from './components/ScheduleFilters'
import type { FilterOption } from './components/ScheduleFilters'
import ScheduleSummary from './components/ScheduleSummary'
import AnalyticsCharts from './components/AnalyticsCharts'

const { RangePicker } = DatePicker

interface RouteParams {
  competitionId: string
  requestId?: string
}

interface AnalyticsFilterValues {
  conference: string[]
  division: string[]
  team: string[]
  location: string[]
  surface: string[]
  status: string[]
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
}

const DEFAULT_ANALYTICS_FILTERS: AnalyticsFilterValues = {
  conference: [],
  division: [],
  team: [],
  location: [],
  surface: [],
  status: [],
  dateRange: null,
}

const ScheduleAnalyticsPage: React.FC = () => {
  const history = useHistory()
  const { competitionId, requestId } = useParams<RouteParams>()

  const [filters, setFilters] = useState<AnalyticsFilterValues>(
    DEFAULT_ANALYTICS_FILTERS
  )

  // TODO: populate from API data
  const conferenceOptions: FilterOption[] = []
  const divisionOptions: FilterOption[] = []
  const teamOptions: FilterOption[] = []
  const locationOptions: FilterOption[] = []
  const surfaceOptions: FilterOption[] = []
  const statusOptions: FilterOption[] = []

  // TODO: populate from API data
  const summaryStats = {
    totalGames: 130,
    totalConflicts: 240,
    totalExceptions: 25,
    scheduleScore: 'ACCEPTABLE',
  }

  const handleBack = () => {
    const basePath = `/scheduler-wizard/${competitionId}/configure`
    history.push(requestId ? `${basePath}/${requestId}` : basePath)
  }

  const handleFilterChange =
    (key: keyof AnalyticsFilterValues) => (value: string[]) => {
      setFilters((prev) => ({ ...prev, [key]: value }))
    }

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    setFilters((prev) => ({ ...prev, dateRange: dates }))
  }

  const handleCardClick = (_cardType: string) => {
    // TODO: handle card click actions
  }

  return (
    <div css={pageContainerStyles}>
      <header css={headerStyles}>
        <div css={headerInnerStyles}>
          <div css={leftSectionStyles}>
            <button
              type='button'
              css={backLinkStyles}
              onClick={handleBack}
              aria-label='Back to schedule preview'
            >
              <Icon
                name='chevron-left'
                fontSize={12}
                color={colors.PRIMARY_300}
              />
              <span css={backLinkTextStyles}>BACK TO SCHEDULE PREVIEW</span>
            </button>
            <h1 css={titleStyles}>Schedule Analytics</h1>
          </div>
        </div>
      </header>

      {/* Divider */}
      <div css={dividerWrapperStyles}>
        <div css={dividerStyles} />
      </div>

      {/* Filter Bar */}
      <div css={filterBarContainerStyles}>
        <div css={filterBarInnerStyles}>
          <FilterDropdown
            label='Conference'
            value={filters.conference}
            allLabel='All Conference'
            options={conferenceOptions}
            onChange={handleFilterChange('conference')}
          />
          <FilterDropdown
            label='Division'
            value={filters.division}
            allLabel='All Divisions'
            options={divisionOptions}
            onChange={handleFilterChange('division')}
          />
          <FilterDropdown
            label='Team'
            value={filters.team}
            allLabel='All Teams'
            options={teamOptions}
            onChange={handleFilterChange('team')}
          />
          <FilterDropdown
            label='Location'
            value={filters.location}
            allLabel='All Locations'
            options={locationOptions}
            onChange={handleFilterChange('location')}
          />
          <FilterDropdown
            label='Surface'
            value={filters.surface}
            allLabel='All Surfaces'
            options={surfaceOptions}
            onChange={handleFilterChange('surface')}
          />
          <FilterDropdown
            label='Status'
            value={filters.status}
            allLabel='All Status'
            options={statusOptions}
            onChange={handleFilterChange('status')}
          />

          {/* Date Range */}
          <div css={dateRangeGroupStyles}>
            <label css={dateRangeLabelStyles}>Date Range</label>
            <RangePicker
              css={dateRangePickerStyles}
              value={filters.dateRange}
              onChange={handleDateRangeChange}
              format='MMM D, YYYY'
              separator='to'
              suffixIcon={
                <Icon name='calendar' fontSize={16} color={colors.WHITE} />
              }
              popupClassName='analytics-date-range-dropdown'
            />
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div css={summarySectionStyles}>
        <div css={summaryInnerStyles}>
          <ScheduleSummary stats={summaryStats} onCardClick={handleCardClick} />
        </div>
      </div>

      {/* Divider */}
      <div css={dividerWrapperStyles}>
        <div css={dividerStyles} />
      </div>

      {/* Charts Section */}
      <div css={chartsSectionStyles}>
        <div css={chartsInnerStyles}>
          <AnalyticsCharts />
        </div>
      </div>
    </div>
  )
}

// ─── Page Layout ───────────────────────────────────────────────

const pageContainerStyles = css`
  min-height: 100vh;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const headerStyles = css`
  background-color: ${colors.SECONDARY_400};
  padding: 40px 88px 40px;
`

const headerInnerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 0;
`

const leftSectionStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const backLinkStyles = css`
  color: ${colors.PRIMARY_300};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 0;
  transition: opacity 0.2s ease;
  line-height: 16.8px;

  &:hover {
    opacity: 0.8;
  }
`

const backLinkTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.PRIMARY_300};
  line-height: 16.8px;
`

const titleStyles = css`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 48px;
  font-weight: 600;
  color: ${colors.WHITE};
  margin: 0;
  line-height: 48px;
`

// ─── Divider ───────────────────────────────────────────────────

const dividerWrapperStyles = css`
  padding: 0 88px;
`

const dividerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
  height: 1px;
  background-color: #3a4659;
`

// ─── Filter Bar ────────────────────────────────────────────────

const filterBarContainerStyles = css`
  padding: 24px 88px;
`

const filterBarInnerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  gap: 16px;
  align-items: flex-end;
`

// ─── Summary Section ───────────────────────────────────────────

const summarySectionStyles = css`
  padding: 24px 88px;
`

const summaryInnerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
`

// ─── Charts Section ────────────────────────────────────────────

const chartsSectionStyles = css`
  padding: 24px 88px;
`

const chartsInnerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
`

// ─── Date Range (only filter not in ScheduleFilters) ───────────

const dateRangeGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
`

const dateRangeLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20.3px;
  color: ${colors.WHITE};
  margin: 0;
`

const dateRangePickerStyles = css`
  &.ant-picker {
    background-color: ${colors.SECONDARY_400};
    border: 1px solid ${colors.SECONDARY_200};
    border-radius: 8px;
    height: 40px;
    padding: 8px 8px 8px 16px;
  }

  &.ant-picker .ant-picker-input > input {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.3px;
    color: ${colors.WHITE};
  }

  &.ant-picker .ant-picker-separator {
    color: ${colors.WHITE};
    font-family: 'Inter', sans-serif;
    font-size: 14px;
  }

  &.ant-picker .ant-picker-suffix {
    color: ${colors.WHITE};
  }

  &.ant-picker:hover {
    border-color: ${colors.SECONDARY_100};
  }

  &.ant-picker-focused {
    border-color: ${colors.PRIMARY_300};
    box-shadow: none;
  }

  &.ant-picker .ant-picker-active-bar {
    background: ${colors.PRIMARY_300};
  }

  &.ant-picker .ant-picker-clear {
    background: ${colors.SECONDARY_400};
    color: ${colors.WHITE};
  }
`

export default ScheduleAnalyticsPage
